
import React from 'react'

import AiHero from '../../Components/Automation/AiHero'
import WhyAIAutomation from '../../Components/Automation/WhyAIAutomation'
import TechServicesSection from '../../Components/Automation/TechServicesSection'
import IndustriesWeServe from '../../Components/Automation/IndustriesWeServe'
import WhyPartnerWithUs from '../../Components/Automation/WhyPartnerWithUs'
import Banner from '../../Components/Banner'
import { Helmet } from 'react-helmet-async'

// Machine Learning & MLOps Features
const featuresData = [
    {
        id: 1,
        title: "Intelligent Predictive Insights",
        description:
            "Turn business data into actionable insights using machine learning models that identify patterns, forecast trends, and support more informed decision-making.",
        image: "/assets/career/img10.png",
    },
    {
        id: 2,
        title: "Scalable Machine Learning Models",
        description:
            "Build and deploy machine learning models designed to handle growing data volumes, evolving business requirements, and changing operational demands.",
        image: "/assets/home-/ai-2.png",
    },
    {
        id: 3,
        title: "Automated ML Workflows",
        description:
            "Streamline model development, testing, deployment, and monitoring with automated machine learning pipelines that improve consistency and reduce repetitive work.",
        image: "/assets/home-/ai-3.png",
    },
    {
        id: 4,
        title: "Continuous Model Improvement",
        description:
            "Monitor model performance, detect data and model drift, and support continuous retraining to keep machine learning solutions reliable as business conditions change.",
        image: "/assets/home-/ai-4.png",
    },
]

// Machine Learning & MLOps Service Offerings
const services = [
    {
        id: 1,
        title: "Machine Learning Development",
        category: "service",
        desc: "Develop custom machine learning models that help businesses uncover patterns, automate predictions, improve operational efficiency, and solve complex business challenges.",
        defaultBg: "bg-gradient-to-b from-[#01386A] to-[#1C355E]",
    },
    {
        id: 2,
        title: "Predictive Analytics",
        category: "service",
        desc: "Use historical and real-time data to forecast demand, identify emerging trends, estimate business outcomes, and support proactive decision-making.",
        defaultBg: "bg-gradient-to-r from-[#01386A] to-[#4B6CB7]",
    },
    {
        id: 3,
        title: "MLOps Pipeline Development",
        category: "service",
        desc: "Build automated machine learning pipelines for data preparation, model training, testing, validation, and deployment to create repeatable ML workflows.",
        defaultBg: "bg-gradient-to-br from-[#01386A] to-[#8E9EAB]",
    },
    {
        id: 4,
        title: "Model Deployment & Serving",
        category: "service",
        desc: "Deploy machine learning models into production environments through scalable serving infrastructure, API integrations, and reliable release workflows.",
        defaultBg: "bg-gradient-to-bl from-[#01386A] to-[#6A85B6]",
    },
    {
        id: 5,
        title: "ML Model Monitoring",
        category: "service",
        desc: "Monitor model performance, prediction quality, data drift, and operational metrics to identify issues and maintain dependable machine learning systems.",
        defaultBg: "bg-gradient-to-tr from-[#01386A] to-[#253B5C]",
    },
    {
        id: 6,
        title: "Data Engineering for ML",
        category: "service",
        desc: "Build dependable data pipelines that collect, transform, validate, and deliver quality data for machine learning development, training, and inference.",
        defaultBg: "bg-gradient-to-t from-[#01386A] to-[#243B55]",
    },
    {
        id: 7,
        title: "Model Optimization & Retraining",
        category: "service",
        desc: "Improve model performance through evaluation, hyperparameter tuning, version management, and retraining workflows aligned with changing data and business requirements.",
        defaultBg: "bg-gradient-to-tl from-[#01386A] to-[#141E30]",
    },
    {
        id: 9,
        title: "Cloud-Based ML Infrastructure",
        category: "service",
        desc: "Design scalable cloud infrastructure for machine learning workloads, including model training, experiment tracking, deployment, resource management, and production operations.",
        defaultBg: "bg-gradient-to-br from-[#004e92] to-[#01386A]",
    },
]

// Machine Learning & MLOps Page
function MachineLearningMLOps() {
    return (
        <div>
            <Helmet>
                <title>
                    Machine Learning & MLOps Services | Mobikode
                </title>

                <meta
                    name="description"
                    content="Mobikode provides machine learning and MLOps services, including custom ML model development, predictive analytics, automated ML pipelines, model deployment, monitoring, and scalable ML infrastructure."
                />

                <meta
                    name="keywords"
                    content="Machine Learning Services, MLOps Services, Machine Learning Development, MLOps Consulting, ML Model Deployment, Machine Learning Pipeline, ML Model Monitoring, Predictive Analytics, Automated Machine Learning, Model Training, Model Versioning, Data Drift Monitoring, ML Infrastructure, Cloud Machine Learning Solutions, Enterprise Machine Learning"
                />

                <link
                    rel="canonical"
                    href="https://www.mobikode.com/machine-learning-mlops"
                />

                <meta
                    property="og:title"
                    content="Machine Learning & MLOps Services | Mobikode"
                />

                <meta
                    property="og:description"
                    content="Build, deploy, and manage production-ready machine learning solutions with Mobikode. Explore ML development, predictive analytics, MLOps automation, model monitoring, and scalable infrastructure."
                />

                <meta
                    property="og:url"
                    content="https://www.mobikode.com/machine-learning-mlops"
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:image"
                    content="https://www.mobikode.com/assets/service-detail/bg3.png"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Machine Learning & MLOps Services | Mobikode"
                />

                <meta
                    name="twitter:description"
                    content="Discover Mobikode's machine learning and MLOps solutions for model development, automated pipelines, production deployment, performance monitoring, and continuous improvement."
                />

                <meta
                    name="twitter:image"
                    content="https://www.mobikode.com/assets/service-detail/bg3.png"
                />
            </Helmet>

            {/* Hero Section */}
            <AiHero
                title="Machine Learning & MLOps"
                text="Data. Intelligence. Innovation."
                imgData="/assets/service-detail/bg3.png"
            />

            {/* Introduction Section */}
            <section className="w-full pt-16 lg:pt-[100px] bg-white overflow-hidden relative">
                <div className="max-w-[1440px] mx-auto px-[15px] lg:px-[80px]">

                    <div className="relative w-full flex flex-col lg:flex-row lg:items-center group lg:h-[507px]">

                        {/* Image Box */}
                        <div className="relative lg:absolute top-0 right-0 w-full lg:max-w-[656px] h-[250px] md:h-[350px] lg:h-full bg-gray-100 overflow-hidden">
                            <img
                                src="/assets/career/img9.jpg"
                                alt="Machine learning model development and MLOps solutions"
                                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>

                        {/* Content Box */}
                        <div className="relative z-20 w-[92%] sm:w-[85%] lg:w-full max-w-[719px] mx-auto lg:mx-0 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] px-[20px] py-[30px] lg:px-[60px] lg:py-[80px] -mt-[40px] lg:mt-0">

                            <h3 className="text-[#01386A] text-[20px] lg:text-[30px] font-[600] font-sora mb-[15px] lg:mb-[35px] leading-[1.3]">
                                Turning Machine Learning into Business Value
                            </h3>

                            <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.6] font-graphik font-[500] text-justify">
                                At Mobikode, we help businesses transform data into intelligent, production-ready machine learning solutions. From developing custom ML models to automating model deployment and monitoring, our approach connects experimentation with real-world business operations. With scalable MLOps workflows, we help teams improve model reliability, simplify maintenance, and manage the machine learning lifecycle more efficiently.
                            </p>

                            {/* Bottom Border Animation */}
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]"></div>

                            <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full"></div>

                        </div>
                    </div>
                </div>
            </section>

            {/* Why Machine Learning & MLOps */}
            <WhyAIAutomation
                title="Why Machine Learning & MLOps with Mobikode?"
                fData={featuresData}
            />

            {/* Service Offerings */}
            <TechServicesSection
                title="Our Machine Learning & MLOps Services"
                sData={services}
            />

            {/* Existing UI Components */}
            <IndustriesWeServe />

            <WhyPartnerWithUs />

            {/* Bottom Banner */}
            <Banner
                title="Machine Learning & MLOps"
                text="Build smarter, scalable, and production-ready machine learning solutions that turn data into measurable business outcomes."
            />
        </div>
    )
}

export default MachineLearningMLOps
